import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NewTableComponent from '../../../components/NewTableComponent';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { categoryStatusOptions, categoryTypeOptions } from '../../../utils/Dropdowns';
import { Badge } from 'react-bootstrap';
import CategoryOverviewCards from './CategoryOverviewCards';
import CreateCategoryModal from './CreateCategoryModal';
import UpdateCategoryModal from './UpdateCategoryModal';
import { plusIcon } from '../../../utils/svgs';
import Button from '../../../components/button';
import { LuPencil } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import CustomModal from '../../../components/CustomModal';
import { deleteCategoryAsync, fetchCategories, getCategoriesOverview } from '../../../store/Slices/CategorySlice';
import { errorNotify, successNotify } from '../../../utils/toast';

const Categories = () => {
    const dispatch = useDispatch();
    const { categoriesData, categoriesLoading, deleteCategoryLoading } = useSelector((state) => state.categories);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [searchFilters, setSearchFilters] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);

    const handleEditCategory = (category) => {
        setSelectedCategory(category);
        setShowUpdateModal(true);
    };

    const handleDeleteCategory = async () => {
        try {
            await dispatch(deleteCategoryAsync(selectedCategoryId)).unwrap();
            successNotify('Category deleted successfully');
            setShowDeleteModal(false);
            setSelectedCategoryId(null);
            
            // Refresh table and overview
            dispatch(fetchCategories({
                page: currentPage,
                limit: limit,
                body: {}
            }));
            dispatch(getCategoriesOverview());
        } catch (error) {
            errorNotify(error);
        }
    };

    useEffect(() => {
        const transformedFilters = {};
        Object.keys(searchFilters).forEach(key => {
            let value = searchFilters[key].value;
            let filterType = searchFilters[key].filter_type || 'contains';

            // Ensure values for 'one_of' are always arrays, and vice versa
            if (Array.isArray(value)) {
                filterType = 'one_of';
            } else if (filterType === 'one_of') {
                value = [value];
            }

            // Specific handling for status/type if needed
            if ((key === 'statusFilter' || key === 'typeFilter') && !Array.isArray(value)) {
                filterType = 'equals';
            }

            transformedFilters[key] = {
                value: value,
                filter_type: filterType
            };
        });

        dispatch(fetchCategories({
            page: currentPage,
            limit: limit,
            body: {
                ...transformedFilters,
                search_text: searchText
            }
        }));
    }, [currentPage, limit, searchText, searchFilters, dispatch]);

    const columns = [
        {
            name: "ID",
            key: "id",
            selector: (row) => row?.id,
            width: "80px",
            tableColumn: true,
            filterName: "idFilter",
            type: "number",
        },
        {
            name: "Title",
            key: "title",
            selector: (row) => row?.title,
            tableColumn: true,
            filterName: "titleFilter",
            type: "text",
        },
        {
            name: "Type",
            key: "type",
            selector: (row) => (
                <Badge bg={row?.type === 'part' ? 'primary' : 'info'} className="text-capitalize">
                    {row?.type}
                </Badge>
            ),
            tableColumn: true,
            filterName: "typeFilter",
            type: "text",
            options: categoryTypeOptions
        },
        {
            name: "Status",
            key: "status",
            selector: (row) => (
                <Badge bg={row?.status === 'active' ? 'success' : 'danger'} className="text-capitalize">
                    {row?.status}
                </Badge>
            ),
            tableColumn: true,
            filterName: "statusFilter",
            type: "text",
            options: categoryStatusOptions
        },
        {
            name: "Created At",
            key: "created_at",
            selector: (row) => row?.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
            width: "150px",
        },
        {
            name: "Actions",
            key: "actions",
            width: "100px",
            selector: (row) => (
                <div className="table_action_btn">
                    <button onClick={() => handleEditCategory(row)} className="edit_btn">
                        <LuPencil />
                    </button>
                    <button onClick={() => {
                        setSelectedCategoryId(row?.id);
                        setShowDeleteModal(true);
                    }} className="delete_btn">
                        <RiDeleteBinLine />
                    </button>
                </div>
            ),
        }
    ];

    return (
        <div className="container-fluid py-4">
            <Breadcrumbs values={['Maintenance', 'Categories']} />

            <div className='page_header access_view mb-4'>
                <h2>Maintenance Categories</h2>
                <div className='d-flex align-items-center gap-2'>
                    <Button
                        submitText={<span>{plusIcon} Add Category</span>}
                        type="button"
                        shouldRow={false}
                        isShowSubmitBtn={true}
                        submitBtn={() => setShowCreateModal(true)}
                        isShowCancel={false}
                    />
                </div>
            </div>
            
            <CategoryOverviewCards />

            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <NewTableComponent
                        pageName="Maintenance Categories"
                        columns={columns}
                        data={categoriesData?.results || []}
                        totalItems={categoriesData?.meta?.totalItems || 0}
                        totalPages={categoriesData?.meta?.totalPages || 0}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        limit={limit}
                        setLimit={setLimit}
                        loading={categoriesLoading}
                        onSearch={(text) => {
                            setSearchText(text);
                            setCurrentPage(1);
                        }}
                        searchFilters={searchFilters}
                        setSearchFilters={setSearchFilters}
                    />
                </div>
            </div>

            <CreateCategoryModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                currentPage={currentPage}
                limit={limit}
            />

            <UpdateCategoryModal
                show={showUpdateModal}
                onHide={() => {
                    setShowUpdateModal(false);
                    setSelectedCategory(null);
                }}
                category={selectedCategory}
                currentPage={currentPage}
                limit={limit}
            />

            <CustomModal show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} size="md" heading="Delete Category" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this Category?</p>

                    <Button
                        cancelBtn={() => setShowDeleteModal(false)}
                        submitBtn={handleDeleteCategory}
                        loading={deleteCategoryLoading}
                        disabled={deleteCategoryLoading}
                        buttonExtraCss="delete_user_modal_btn"
                        submitText="Delete"
                        type="submit"
                        isShowCancel={true}
                    />
                </div>
            </CustomModal>
        </div>
    );
};

export default Categories;
